import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { Button } from '../components/ui/button';
import UserNavMenu from '../components/UserNavMenu';
import ThemeToggle from '../components/ThemeToggle';
import ShareDialog from '../components/ShareDialog';
import { Onboarding, shouldShowOnboarding } from '../components/Onboarding';
import axios from 'axios';
import { 
  Sparkles, Plus, FileText, Trash2, Loader2, 
  Crown, BarChart3, ArrowRight, Heart, Share2, Pin, ShoppingBag, Package, Shield
} from 'lucide-react';
import { openPinterestStudio } from '../utils/pinterestStudioHelper';
import LumeraLogo from '../components/LumeraLogo';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import MyLicenses from '../components/MyLicenses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isPro, loading: authLoading, purchases, fetchPurchases } = useAuth();
  const { count: favCount } = useFavorites();
  const [projects, setProjects] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [sharingProject, setSharingProject] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'purchases' ? 'purchases' :
    searchParams.get('tab') === 'licenses' ? 'licenses' : 'projects'
  );

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, usageRes] = await Promise.all([
        axios.get(`${API}/projects/`),
        axios.get(`${API}/content/usage`)
      ]);
      setProjects(projectsRes.data);
      setUsage(usageRes.data);
    } catch (err) { console.error('Failed to fetch dashboard data:', err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    if (shouldShowOnboarding(user)) setShowOnboarding(true);
  }, [user, authLoading, navigate, fetchData]);

  const handleDelete = async (projectId) => {
    setDeleting(projectId);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Delete failed:', error);
    }
    setDeleting(null);
  };

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
      {/* Onboarding overlay */}
      {showOnboarding && <Onboarding user={user} onComplete={() => setShowOnboarding(false)} />}

      {/* Top Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <div className="flex items-center gap-4">
            {!isPro && (
              <Button data-testid="upgrade-btn" variant="ghost" onClick={() => navigate('/pricing')} className="text-primary font-sans font-semibold text-sm hover:bg-primary/5 hidden sm:flex">
                <Crown className="w-4 h-4 mr-1.5" /> Upgrade to Pro
              </Button>
            )}
            {isPro && <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full hidden sm:inline">PRO</span>}
            <ThemeToggle />
            <UserNavMenu size={36} />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Header with avatar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div className="flex items-center gap-4">
            {user.avatar_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}?_t=${Date.now()}`}
                alt=""
                data-testid="dashboard-avatar"
                className="w-12 h-12 rounded-full object-cover border-2 border-stone-200 hidden sm:block"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span
                data-testid="dashboard-avatar-initials"
                className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-[#B49A88] to-[#B49A88] text-white font-serif font-bold text-lg select-none hidden sm:flex"
              >
                {user.full_name ? user.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : user.email[0].toUpperCase()}
              </span>
            )}
            <div>
              <h1 className="text-3xl font-serif font-bold text-stone-900">
                {user.full_name ? `Hi, ${user.full_name.split(' ')[0]}` : 'Dashboard'}
              </h1>
              <p className="text-stone-600 font-sans mt-1">Manage your digital products</p>
            </div>
          </div>
          <Button data-testid="new-product-btn" onClick={() => navigate('/generator')} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-semibold px-6 py-3 rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Create new product
          </Button>
        </div>

        {/* Email verification banner */}
        <div className="mb-6">
          <EmailVerificationBanner />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-sans font-medium text-stone-500">Products created</span>
            </div>
            <p data-testid="projects-count" className="text-3xl font-serif font-bold text-stone-900">{projects.length}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-accent" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-sans font-medium text-stone-500">Generations this month</span>
            </div>
            <p data-testid="usage-count" className="text-3xl font-serif font-bold text-stone-900">
              {usage ? `${usage.used} / ${usage.limit === -1 ? '\u221E' : usage.limit}` : '...'}
            </p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <Crown className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-sans font-medium text-stone-500">Subscription</span>
            </div>
            <p data-testid="plan-type" className="text-3xl font-serif font-bold text-stone-900 capitalize">{user.subscription_tier}</p>
            {!isPro && (
              <button onClick={() => navigate('/pricing')} className="text-sm text-primary font-sans font-semibold mt-1 hover:underline">
                Upgrade for unlimited
              </button>
            )}
          </div>
          <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/favorites')}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-sans font-medium text-stone-500">Favorites</span>
            </div>
            <p data-testid="favorites-count" className="text-3xl font-serif font-bold text-stone-900">{favCount}</p>
          </div>
        </div>

        {/* Pinterest Studio CTA */}
        <div
          onClick={() => navigate('/pinterest-studio')}
          className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 mb-10 flex items-center justify-between cursor-pointer hover:shadow-md hover:border-stone-200 transition-all group"
          data-testid="pinterest-studio-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#E60023]/10 flex items-center justify-center flex-shrink-0">
              <Pin className="w-5 h-5 text-[#E60023]" />
            </div>
            <div>
              <h3 className="font-sans font-bold text-stone-800 text-sm">Pinterest Studio</h3>
              <p className="font-sans text-xs text-stone-500">Create, optimize and publish Pinterest content directly from Lumera</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </div>

        {/* Projects List */}
        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-white rounded-full p-1 border border-stone-100 shadow-sm mb-8 w-fit">
          <button
            data-testid="tab-projects"
            onClick={() => setActiveTab('projects')}
            className={`px-5 py-2 rounded-full text-sm font-sans font-semibold transition-all ${
              activeTab === 'projects' ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />My products
          </button>
          <button
            data-testid="tab-purchases"
            onClick={() => setActiveTab('purchases')}
            className={`px-5 py-2 rounded-full text-sm font-sans font-semibold transition-all ${
              activeTab === 'purchases' ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <ShoppingBag className="w-4 h-4 inline mr-1.5 -mt-0.5" />My purchases{purchases.length > 0 && ` (${purchases.length})`}
          </button>
          <button
            data-testid="tab-licenses"
            onClick={() => setActiveTab('licenses')}
            className={`px-5 py-2 rounded-full text-sm font-sans font-semibold transition-all ${
              activeTab === 'licenses' ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-1.5 -mt-0.5" />Licenses
          </button>
        </div>

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div>
            {purchases.length === 0 ? (
              <div data-testid="empty-purchases" className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <ShoppingBag className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">No purchases yet</h3>
                <p className="text-stone-600 font-sans mb-6">Buy individual products or collections from the storefront to get permanent access.</p>
                <Button data-testid="browse-products-btn" onClick={() => navigate('/')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
                  Explore products <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Collections */}
                {purchases.some(p => p.purchase_type === 'bundle') && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="w-4 h-4 text-[#B49A88]" />
                      <h3 className="font-sans font-bold text-stone-800 text-sm uppercase tracking-wider">Your Collections</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {purchases.filter(p => p.purchase_type === 'bundle').map((purchase) => (
                        <div
                          key={purchase.id}
                          data-testid={`bundle-purchase-card-${purchase.product_slug}`}
                          className="bg-white p-6 rounded-2xl border border-[#B49A88]/20 shadow-sm hover:shadow-md transition-all group"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-10 h-10 rounded-xl bg-[#B49A88]/10 flex items-center justify-center">
                              <Package className="w-5 h-5 text-[#B49A88]" strokeWidth={1.5} />
                            </div>
                            <span className="text-[10px] font-sans font-bold text-[#B49A88] bg-[#B49A88]/10 px-2 py-0.5 rounded-full uppercase">Collection</span>
                          </div>
                          <h3 className="font-sans font-semibold text-stone-800 mb-1">{purchase.product_title}</h3>
                          {purchase.bundle_product_slugs && (
                            <p className="text-xs font-sans text-stone-400 mb-1">{purchase.bundle_product_slugs.length} products included</p>
                          )}
                          <p className="text-sm font-sans text-stone-500 mb-1">
                            {purchase.currency === 'nok' ? `${purchase.amount} kr` : `$${purchase.amount}`}
                            {' '}&middot;{' '}{purchase.payment_provider === 'stripe' ? 'Stripe' : 'PayPal'}
                          </p>
                          <p className="text-xs font-sans text-stone-400 mb-4">
                            {new Date(purchase.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          <Button
                            data-testid={`use-bundle-${purchase.product_slug}`}
                            onClick={() => navigate('/')}
                            className="w-full bg-[#B49A88] text-white hover:bg-[#B49A88]/85 rounded-xl py-5 font-sans font-semibold text-sm"
                          >
                            Explore products <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Individual products */}
                {purchases.some(p => p.purchase_type !== 'bundle') && (
                  <div>
                    {purchases.some(p => p.purchase_type === 'bundle') && (
                      <div className="flex items-center gap-2 mb-4">
                        <ShoppingBag className="w-4 h-4 text-emerald-600" />
                        <h3 className="font-sans font-bold text-stone-800 text-sm uppercase tracking-wider">Individual Products</h3>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {purchases.filter(p => p.purchase_type !== 'bundle').map((purchase) => (
                        <div
                          key={purchase.id}
                          data-testid={`purchase-card-${purchase.product_slug}`}
                          className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm hover:shadow-md transition-all group"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                              <ShoppingBag className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                            </div>
                            <span className="text-[10px] font-sans font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase">Purchased</span>
                          </div>
                          <h3 className="font-sans font-semibold text-stone-800 mb-1">{purchase.product_title}</h3>
                          <p className="text-sm font-sans text-stone-500 mb-1">
                            {purchase.currency === 'nok' ? `${purchase.amount} kr` : `$${purchase.amount}`}
                            {' '}&middot;{' '}{purchase.payment_provider === 'stripe' ? 'Stripe' : 'PayPal'}
                          </p>
                          <p className="text-xs font-sans text-stone-400 mb-4">
                            {new Date(purchase.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          <Button
                            data-testid={`use-purchase-${purchase.product_slug}`}
                            onClick={() => navigate(`/generator?prefill_topic=${encodeURIComponent(purchase.product_title)}`)}
                            className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-xl py-5 font-sans font-semibold text-sm"
                          >
                            Use product <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Licenses Tab */}
        {activeTab === 'licenses' && (
          <MyLicenses />
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (<>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div data-testid="empty-projects" className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">No products yet</h3>
            <p className="text-stone-600 font-sans mb-6">Create your first digital product to get started.</p>
            <Button data-testid="empty-create-btn" onClick={() => navigate('/generator')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
              Create your first product <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-card-${project.id}`}
                onClick={() => navigate(`/generator?project_id=${project.id}`)}
                className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm hover:shadow-md transition-all group cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <button
                    data-testid={`delete-project-${project.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                    disabled={deleting === project.id}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-red-500"
                  >
                    {deleting === project.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
                <h3 className="font-sans font-semibold text-stone-800 mb-1">{project.title}</h3>
                <p className="text-sm font-sans text-stone-500 capitalize mb-3">{project.product_type?.replace(/-/g, ' ')}</p>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-sans text-stone-400">
                    {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`pinterest-project-${project.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPinterestStudio(navigate, {
                          title: project.title,
                          description: project.description || '',
                          category: project.product_type?.replace(/-/g, ' ') || '',
                          id: project.id,
                        });
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-[#E60023]"
                      aria-label="Promote on Pinterest"
                      title="Promote on Pinterest"
                    >
                      <Pin className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`share-project-${project.id}`}
                      onClick={(e) => { e.stopPropagation(); setSharingProject(project); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-primary"
                      aria-label="Share product"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>

      {/* Share Dialog */}
      <ShareDialog
        open={!!sharingProject}
        onOpenChange={(open) => { if (!open) setSharingProject(null); }}
        project={sharingProject}
      />
    </div>
  );
};

export default Dashboard;
